import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";  
import { toast } from "react-toastify";
import { validateEmail } from "../../utils/passwordValidation";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const RegisterInstitute = () => {
  const [formData, setFormData] = useState({
    name: "",
    type_of_institute: "",
    email: "",
    password: "",
    confirmPassword: "",
    date_established: "",
    contact_person: "",
    telephone: [""],
    country: "Pakistan",
    city: "Islamabad",
    area: "",
    street: "",
    houseNo: "",
    coordinates: "33.6844, 73.0479",
    website: "",
    instagram_url: "",
    facebook_url: "",
    x_url: "",
    about_institute: "",
    company_registration_number: "",
    logo: null,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [instituteTypes, setInstituteTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return "Phone number is required";
    // Accept both mobile (0XXX-XXXXXXX) and landline (0XX-XXXXXXX) formats
    const mobileRegex = /^0\d{3}-\d{7}$/; // Mobile format
    const landlineRegex = /^0\d{2,3}-\d{7}$/; // Landline format
    if (!mobileRegex.test(phone) && !landlineRegex.test(phone)) {
      return "Phone number must be in format 0XXX-XXXXXXX (mobile) or 0XX-XXXXXXX (landline)";
    }
    return "";
  };

  const validateAtLeastOnePhone = (telephones) => {
    if (!telephones || telephones.length === 0 || !telephones[0].trim()) {
      return "At least one telephone number is required";
    }
    return "";
  };


  const [mapCenter, setMapCenter] = useState([33.6844, 73.0479]); // Islamabad, Pakistan coordinates
  const [marker, setMarker] = useState([33.6844, 73.0479]);
  const [shouldPan, setShouldPan] = useState(false);
  const mapRef = useRef(null);

  const SetViewOnClick = ({ coords, shouldPan }) => {
    const map = useMap();
    useEffect(() => {
      if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
        map.setView(coords, 13);
      }
    }, [coords, map, shouldPan]);
    return null;
  };

  const geocodeAddress = useCallback(async () => {
    const { country, city, area, street, houseNo } = formData;
    const address = `${houseNo} ${street}, ${area}, ${city}, ${country}`.trim();
    if (!address) return;

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: address,
            format: "json",
            limit: 1,
            addressdetails: 1,
          },
        }
      );

      if (response.data.length > 0) {
        const { lat, lon, address: details } = response.data[0];
        const newCoords = [parseFloat(lat), parseFloat(lon)];
        setMapCenter(newCoords);
        setMarker(newCoords);
        setShouldPan(true);
        setFormData((prevData) => ({
          ...prevData,
          country: details.country || prevData.country,
          city: details.city || details.town || prevData.city,
          area: details.suburb || prevData.area,
          street: details.road || prevData.street,
          houseNo: details.house_number || prevData.houseNo,
          coordinates: `${lat}, ${lon}`,
        }));
      } else {
        console.error("No location found for this address");
      }
    } catch (error) {
      console.error("Error geocoding:", error);
    }
  }, [formData]);

  const handleMapClick = useCallback(async (e) => {
    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setMapCenter([lat, lng]);
    setShouldPan(true);
    setFormData((prevData) => ({
      ...prevData,
      coordinates: `${lat}, ${lng}`,
    }));

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: {
            lat: lat,
            lon: lng,
            format: "json",
            addressdetails: 1,
          },
        }
      );

      if (response.data) {
        const { address } = response.data;
        setFormData((prevData) => ({
          ...prevData,
          country: address.country || prevData.country,
          city: address.city || address.town || prevData.city,
          area:
            address.suburb ||
            address.neighbourhood ||
            address.county ||
            prevData.area,
          street: address.road || prevData.street,
          houseNo: address.house_number || prevData.houseNo,
          coordinates: `${lat}, ${lng}`,
        }));
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
    }
  }, []);

  const MapEvents = () => {
    useMapEvents({
      click: handleMapClick,
      dragstart: () => setShouldPan(false),
      zoomstart: () => setShouldPan(false),
    });
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const typesResponse = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/institute/get-institute-types`
        );

        setInstituteTypes(typesResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, logo: file });

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTelephoneChange = (index, value) => {
    const newTelephones = [...formData.telephone];
    newTelephones[index] = value;
    setFormData({ ...formData, telephone: newTelephones });
  };

  const addTelephone = () => {
    setFormData({ ...formData, telephone: [...formData.telephone, ""] });
  };

  const removeTelephone = (index) => {
    if (formData.telephone.length > 1) {
      const newTelephones = formData.telephone.filter((_, i) => i !== index);
      setFormData({ ...formData, telephone: newTelephones });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};

    // Required field validations
    newErrors.name = validateRequiredField(formData.name, "Institute name");
    newErrors.type_of_institute = validateRequiredField(formData.type_of_institute, "Type of institute");
    newErrors.email = formData.email ? validateEmail(formData.email) : "Email is required";
    newErrors.password = (!formData.password || formData.password.length < 6)
      ? "Password must be at least 6 characters long"
      : "";
    newErrors.date_established = validateRequiredField(formData.date_established, "Date of establishment");
    newErrors.contact_person = validateRequiredField(formData.contact_person, "Contact person");

    // Phone validation
    newErrors.telephone = validateAtLeastOnePhone(formData.telephone);

    // Address validations (street and house are optional)
    newErrors.country = validateRequiredField(formData.country, "Country");
    newErrors.city = validateRequiredField(formData.city, "City");

    // Logo validation
    if (!formData.logo) {
      newErrors.logo = "Institute logo is required";
    }

    // Password match validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(newErrors).some(error => error !== "");
    if (hasErrors) {
      toast.error("Please fix the errors in the form before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();

      // Add form data
      Object.keys(formData).forEach((key) => {
        if (key === "telephone") {
          data.append(key, JSON.stringify(formData[key]));
        } else if (key === "logo" && formData[key]) {
          data.append("logo", formData[key]);
        } else if (key !== "logo" && key !== "confirmPassword") {
          data.append(key, formData[key]);
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/institute/register-institute`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Institute registered successfully! Please wait for admin approval.");
      navigate("/institute-dashboard");
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(`Registration failed: ${error.response.data.message}`);
      } else {
        toast.error("Error registering institute. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/logo.png" 
            alt="Logo" 
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" 
            onClick={() => navigate(-1)}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Register as an Institute
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Join our community and connect with athletes
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20"
        >
          {/* Basic Information */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.name 
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter institute name"
                />
                {errors.name && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type of Institute *
                </label>
                <select
                  name="type_of_institute"
                  required
                  value={formData.type_of_institute}
                  onChange={handleInputChange}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.type_of_institute 
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                >
                  <option value="">Select Type</option>
                  {instituteTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.type_of_institute && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.type_of_institute}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Establishment *
                </label>
                <input
                  type="date"
                  name="date_established"
                  required
                  value={formData.date_established}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person *
                </label>
                <input
                  type="text"
                  name="contact_person"
                  required
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.contact_person 
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter contact person name"
                />
                {errors.contact_person && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.contact_person}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                    errors.email 
                      ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                      : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  }`}
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.email}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      errors.password 
                        ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                    placeholder="Enter password"
                  />
                  {errors.password && (
                    <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                      <span className="text-xs">⚠</span>
                      {errors.password}
                    </div>
                  )}
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full h-11 px-3 pr-12 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                      errors.confirmPassword 
                        ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                        : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    }`}
                    placeholder="Confirm password"
                  />
                  {errors.confirmPassword && (
                    <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                      <span className="text-xs">⚠</span>
                      {errors.confirmPassword}
                    </div>
                  )}
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Institute Logo <span className="text-red-500">*</span>
            </label>
            {errors.logo && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {errors.logo}
              </div>
            )}
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="logo-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                style={{
                  backgroundImage: logoPreview ? `url(${logoPreview})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {!logoPreview && (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">
                        Click to upload logo
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG or JPEG (MAX. 10MB)
                    </p>
                  </div>
                )}
                <input
                  id="logo-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  required
                />
              </label>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Contact Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telephone Numbers <span className="text-red-500">*</span>
                </label>
                {errors.telephone && (
                  <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                    <span className="text-xs">⚠</span>
                    {errors.telephone}
                  </div>
                )}
                {formData.telephone.map((tel, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="tel"
                      value={tel}
                      onChange={(e) =>
                        handleTelephoneChange(index, e.target.value)
                      }
                      className={`flex-1 h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                        errors[`telephone_${index}`] 
                          ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                          : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                      }`}
                      placeholder="0XXX-XXXXXXX or 0XX-XXXXXXX"
                      required={index === 0}
                    />
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeTelephone(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    )}
                    {index === formData.telephone.length - 1 && (
                      <button
                        type="button"
                        onClick={addTelephone}
                        className="px-3 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    name="instagram_url"
                    value={formData.instagram_url}
                    onChange={handleInputChange}
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    placeholder="https://instagram.com/username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    name="facebook_url"
                    value={formData.facebook_url}
                    onChange={handleInputChange}
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                    placeholder="https://facebook.com/page"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  X (Twitter) URL
                </label>
                <input
                  type="url"
                  name="x_url"
                  value={formData.x_url}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="https://x.com/username"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Address Information
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <input
                  type="text"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area
                </label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter area"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter street"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House/Plot No <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="houseNo"
                  value={formData.houseNo}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter house/plot number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coordinates
                </label>
                <input
                  type="text"
                  name="coordinates"
                  value={formData.coordinates}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa]"
                  placeholder="Click on map or find coordinates"
                  readOnly
                />
              </div>
            </div>

            <button
              type="button"
              onClick={geocodeAddress}
              className="mb-4 px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] focus:outline-none transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
            >
              Find Coordinates from Address
            </button>

            <div className="h-96 w-full rounded-lg overflow-hidden border border-gray-300">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {marker && <Marker position={marker} />}
                <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
                <MapEvents />
              </MapContainer>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Additional Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About the Institute <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  name="about_institute"
                  value={formData.about_institute}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Tell us about your institute, its mission, vision, and achievements..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Registration Number <span className="text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="company_registration_number"
                  value={formData.company_registration_number}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  placeholder="Enter registration number (if applicable)"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 flex justify-center items-center border border-transparent rounded-lg text-base font-semibold text-white bg-[#3498db] hover:bg-[#2980b9] focus:outline-none transition-all duration-300 uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? "REGISTERING..." : "Register Institute"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterInstitute;
